/**
 * The roles a channel's composer offers under `@`.
 *
 * Derived from the channel's role keys, which is all `/app` receives
 * (docs/adr/24-what-the-channel-list-sends-to-the-client.md sends keys, never
 * permission values). Each key is looked up rather than taken apart
 * (TECH-3336): a roster role becomes a mentionable row named after the role,
 * the workspace's own "everyone" key becomes the everyone row, and anything
 * else — a channel pseudo-role, a role from another workspace, a role deleted
 * since the grant — is skipped.
 *
 * Lived twice, identically, in `channel-view.tsx` and `thread-creation-panel.tsx`.
 */
import { useMemo } from "react";
import { useStore } from "zustand";
import { useRoleKeys } from "@/hooks/use-role-keys";
import { coreStore } from "@/lib/stores/core-store";

export type MentionRoleOption = {
	id: string;
	roleType: "workspace";
	role: string;
	workspaceId: string | undefined;
};

export function useMentionRoles(channelId: string): MentionRoleOption[] {
	const channel = useStore(coreStore, (state) => state.channels.get(channelId));
	const workspaceId = channel?.workspaceId ?? undefined;
	const { classify } = useRoleKeys(workspaceId);
	const roleKeys = channel?.roleKeys;

	return useMemo(() => {
		if (!roleKeys) return [];

		const result: MentionRoleOption[] = [];
		for (const key of roleKeys) {
			const found = classify(key);
			if (found.kind === "skip") continue;
			if (found.kind === "everyone") {
				result.push({
					// The everyone row is addressed by its key, because there is no
					// `Role` record behind it
					// (docs/adr/11-workspace-participation-permission-structure.md).
					id: key,
					roleType: "workspace",
					role: "everyone",
					workspaceId,
				});
				continue;
			}
			result.push({
				id: found.role.id,
				roleType: "workspace",
				role: found.role.name,
				workspaceId,
			});
		}
		return result;
	}, [roleKeys, classify, workspaceId]);
}
